/**
 * Document 2 · Phase 4.4b — Renewal / Lapse Banner
 *
 * Compact app-wide banner that surfaces when the current user's billing
 * entitlement is in a state requiring action:
 *   - source = 'stripe' AND status past_due  → "Payment failed" red band
 *   - source = 'lapsed'                      → "Read-only mode" zinc band
 *   - source = 'trial' AND trial_days_left ≤ 3 → "Trial ending" amber band
 *
 * Hidden on:
 *   - active stripe subs
 *   - admin overrides / company-provided access
 *   - early trial (>3 days left)
 *
 * One-click "Reactivate" opens the Stripe Customer Portal in a new tab.
 * Trial banner CTA links to the public /pricing page.
 *
 * No data is mutated; data preservation contract is intact.
 */
import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, Clock, Sparkles, Loader2, X, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';


const DISMISS_KEY = 'dh_renewal_banner_dismissed_v1';


function getDismissedUntil() {
  try {
    const raw = sessionStorage.getItem(DISMISS_KEY);
    if (!raw) return 0;
    const v = parseInt(raw, 10);
    return Number.isFinite(v) ? v : 0;
  } catch {
    return 0;
  }
}


export default function RenewalBanner() {
  const api = useApi();
  const { token, user } = useAuth();
  const [ent, setEnt] = useState(null);
  const [opening, setOpening] = useState(false);
  const [dismissedAt, setDismissedAt] = useState(getDismissedUntil());

  const load = useCallback(async () => {
    try {
      const data = await api.get('/billing/entitlement');
      setEnt(data);
    } catch {
      // Silent — never block the app on banner load
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user) return;
    load();
  }, [user, load]);

  const dismiss = () => {
    const until = Date.now() + 1000 * 60 * 60 * 12; // 12h
    try { sessionStorage.setItem(DISMISS_KEY, String(until)); } catch { /* ignore */ }
    setDismissedAt(until);
  };

  const openPortal = async () => {
    setOpening(true);
    try {
      const BACKEND = process.env.REACT_APP_BACKEND_URL;
      const res = await axios.post(
        `${BACKEND}/api/billing/portal`,
        { return_path: '/dashboard' },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data?.portal_url) {
        window.open(res.data.portal_url, '_blank', 'noopener,noreferrer');
      } else {
        toast.error('Could not open portal');
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || 'Could not open portal');
      toast.error(msg);
    } finally {
      setOpening(false);
    }
  };

  // Hide while loading, when no user, when manually dismissed
  if (!user || !ent) return null;
  if (dismissedAt && dismissedAt > Date.now()) return null;

  const isStripePastDue = ent.source === 'stripe' && ent.is_lapsed;
  const isLapsed = ent.source === 'lapsed';
  const isTrialEnding = ent.source === 'trial' && (ent.trial_days_left ?? 99) <= 3;

  if (!(isStripePastDue || isLapsed || isTrialEnding)) return null;

  // ── Variants ──
  let icon = <AlertTriangle className="w-4 h-4" />;
  let bgClass = 'bg-red-50 border-red-300 text-red-900';
  let title = '';
  let body = '';
  let cta = null;

  if (isStripePastDue) {
    icon = <AlertTriangle className="w-4 h-4 flex-shrink-0" />;
    bgClass = 'bg-red-50 border-red-300 text-red-900';
    title = 'Payment failed — your subscription is past due';
    body = 'Update your card to keep AI features active. Your data is safe and unchanged.';
    cta = (
      <Button
        size="sm"
        onClick={openPortal}
        disabled={opening}
        className="rounded-sm h-7 text-xs"
        data-testid="renewal-banner-update-payment-btn"
      >
        {opening
          ? (<><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Opening…</>)
          : (<>Update payment <ExternalLink className="w-3 h-3 ml-1" /></>)
        }
      </Button>
    );
  } else if (isLapsed) {
    icon = <Clock className="w-4 h-4 flex-shrink-0" />;
    bgClass = 'bg-zinc-50 border-zinc-300 text-zinc-800';
    title = 'Read-only mode — your subscription has lapsed';
    body = 'Your data is preserved. Reactivate to unlock AI generation and Vision Decks.';
    cta = (
      <Link to="/pricing">
        <Button
          size="sm"
          className="rounded-sm h-7 text-xs"
          style={{ backgroundColor: '#B89B5E', color: 'white' }}
          data-testid="renewal-banner-reactivate-btn"
        >
          Reactivate
        </Button>
      </Link>
    );
  } else if (isTrialEnding) {
    icon = <Sparkles className="w-4 h-4 flex-shrink-0" />;
    bgClass = 'bg-amber-50 border-amber-300 text-amber-900';
    const days = ent.trial_days_left;
    title = days > 0
      ? `${days} day${days === 1 ? '' : 's'} left in your free trial`
      : 'Trial ends today';
    body = 'Pick a plan to keep using DH Design after your trial ends.';
    cta = (
      <Link to="/pricing">
        <Button
          size="sm"
          className="rounded-sm h-7 text-xs"
          style={{ backgroundColor: '#B89B5E', color: 'white' }}
          data-testid="renewal-banner-pick-plan-btn"
        >
          Pick a plan
        </Button>
      </Link>
    );
  }

  return (
    <div
      className={`border-b ${bgClass}`}
      role="status"
      aria-live="polite"
      data-testid="renewal-banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center gap-3 flex-wrap text-sm">
        {icon}
        <div className="flex-1 min-w-0">
          <span className="font-semibold">{title}.</span>{' '}
          <span className="opacity-80">{body}</span>
        </div>
        <div className="flex items-center gap-1.5">
          {cta}
          <button
            onClick={dismiss}
            className="opacity-60 hover:opacity-100 p-1 rounded-sm"
            aria-label="Dismiss"
            data-testid="renewal-banner-dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
